import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useBrand } from "../lib/brand";
import { isClientView } from "../lib/view-mode";
import { MessageCircle } from "lucide-react";

const WHATSAPP_DISPLAY = "+94 (0) 77 635 6704";
const WHATSAPP_NUMBER = "94776356704";
const WHATSAPP_MESSAGE = encodeURIComponent("Hi, I'd like to know more about BizPulse AI");
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

export default function Footer() {
  const nav = useNavigate();
  const loc = useLocation();
  const { tenant } = useBrand();
  const onLanding = loc.pathname === "/";
  const clientView = isClientView(loc.pathname, tenant);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    if (!onLanding) {
      nav("/", { state: { scrollTo: id } });
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Client-view footer: minimal, no BizPulse marketing
  if (clientView) {
    return (
      <footer className="border-t border-border/40 mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {tenant ? (
              <span>
                © {new Date().getFullYear()} {tenant.company || tenant.name}
              </span>
            ) : (
              <span>© {new Date().getFullYear()}</span>
            )}
            <span className="opacity-60">·</span>
            <Link to="/" className="hover:text-foreground transition-colors">
              Powered by <span className="text-accent">BizPulse AI</span>
            </Link>
          </div>
          {/* Show the consultant's own contact info if they have a WhatsApp / booking link */}
          {tenant && (tenant.whatsapp || tenant.booking_link) ? (
            <div className="flex items-center gap-4">
              {tenant.whatsapp ? (
                <a href={`https://wa.me/${tenant.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 hover:text-foreground" data-testid="client-footer-whatsapp">
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-400" /> {tenant.whatsapp}
                </a>
              ) : null}
              {tenant.booking_link ? (
                <a href={tenant.booking_link} target="_blank" rel="noreferrer" className="hover:text-foreground" data-testid="client-footer-booking">
                  Book a call
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      </footer>
    );
  }

  // Full marketing footer
  return (
    <footer className="border-t border-border/60 mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-4 gap-8 text-sm">
        <div className="md:col-span-2">
          <div className="font-sans-ui font-bold text-lg">BizPulse <span className="text-accent">AI</span></div>
          <p className="text-muted-foreground mt-3 max-w-sm leading-relaxed">
            Discover Your Business Opportunities. Connect With the Right Expert. AI-powered diagnostics
            tailored by country, industry, and business function.
          </p>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-whatsapp"
            className="mt-5 inline-flex items-center gap-3 group"
          >
            <span className="w-10 h-10 rounded-full bg-emerald-500 group-hover:bg-emerald-400 transition-colors flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5 text-white" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-widest text-muted-foreground">WhatsApp</span>
              <span className="block text-foreground group-hover:text-accent transition-colors font-medium">
                {WHATSAPP_DISPLAY}
              </span>
            </span>
          </a>
        </div>

        <div>
          <div className="overline mb-3">Product</div>
          <ul className="space-y-2 text-muted-foreground">
            <li><a href="/#features" onClick={scrollTo("features")} className="hover:text-foreground cursor-pointer" data-testid="footer-features">Features</a></li>
            <li><Link to="/pricing" className="hover:text-foreground" data-testid="footer-pricing">Pricing</Link></li>
            <li><a href="/#contact" onClick={scrollTo("contact")} className="hover:text-foreground cursor-pointer" data-testid="footer-contact">Contact</a></li>
            <li><Link to="/sample" className="hover:text-foreground">Sample Discovery</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline mb-3">Company</div>
          <ul className="space-y-2 text-muted-foreground">
            <li><Link to="/register" className="hover:text-foreground">For Consultants</Link></li>
            <li><Link to="/login" className="hover:text-foreground">Sign in</Link></li>
            <li><Link to="/terms" className="hover:text-foreground" data-testid="footer-terms">Terms of Service</Link></li>
            <li><Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/refund-policy" className="hover:text-foreground" data-testid="footer-refund">Refund Policy</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border/40 py-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} BizPulse AI · BizPulseAI.net
      </div>
    </footer>
  );
}
